import type { AlertDialog } from '@base-ui/react';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { Product } from '~/api/products/types';

import { useProducts } from '~/api/products';
import { useUser } from '~/api/user';
import { createAppColumnHelper } from '~/components';
import { ColumnIds } from '~/constants/table';
import { useIntl } from '~/hooks/useIntl';
import { useProductCategories } from '~/hooks/useProductCategories';

import { ProductActivityTypeCell } from './ProductsTableCells/ProductActivityTypeCell';
import { ProductCategoriesCell } from './ProductsTableCells/ProductCategoriesCell';
import { ProductMobileCell } from './ProductsTableCells/ProductMobileCell';
import { ProductNameCell } from './ProductsTableCells/ProductNameCell';
import { ProductOptionsCell } from './ProductsTableCells/ProductOptionsCell';
import { ProductPriceCell } from './ProductsTableCells/ProductPriceCell';
import { ProductStatusCell } from './ProductsTableCells/ProductStatusCell';

export type DialogHandles = Record<'deleteProductHandle', AlertDialog.Handle<Product>>;

const columnHelper = createAppColumnHelper<Product>();

export const useProductsTableColumns = ({ dialogHandles }: { dialogHandles: DialogHandles }) => {
  const { data: user } = useUser();
  const filters = useProductsTableFilters();

  const { t } = useTranslation(['common', 'lines', 'products']);

  return useMemo(
    () =>
      columnHelper.columns([
        // Name
        columnHelper.accessor(({ name }) => name[user.lang], {
          id: ColumnIds.productsName,
          header: t('products:overview.columns.name'),
          cell: ({ getValue }) => <ProductNameCell name={getValue()} />,
          meta: {
            styles: { size: '2fr', minSize: 100 },
            isResizable: true,
          },
        }),

        // Status
        columnHelper.accessor('status', {
          id: ColumnIds.productsStatus,
          header: t('products:overview.columns.status.title'),
          cell: ({ getValue }) => <ProductStatusCell status={getValue()} />,
          filterFn: 'equalsOneOf',
          sortFn: (a, b) => {
            const priority = { InReview: 3, Approved: 2, Rejected: 1 } as const;
            return priority[a.original.status] > priority[b.original.status] ? -1 : 1;
          },
          enableGlobalFilter: false,
          meta: {
            styles: { size: 'max-content', minSize: 75 },
            isResizable: true,
            filterOptions: filters.statuses,
          },
        }),

        // Activity type
        columnHelper.accessor(({ activityType }: Product) => t(`lines:creativeWork.options.${activityType}.label`), {
          id: ColumnIds.productsActivityType,
          header: t('products:overview.columns.creativeWork'),
          cell: ({ getValue }) => <ProductActivityTypeCell activityType={getValue()} />,
          filterFn: 'equalsOneOf',
          enableGlobalFilter: false,
          meta: {
            styles: { size: 'max-content', minSize: 100 },
            isResizable: true,
            filterOptions: filters.activityTypes,
          },
        }),

        // Price
        columnHelper.accessor(({ price }) => price ?? undefined, {
          id: ColumnIds.productsPrice,
          header: t('products:overview.columns.price'),
          cell: ({ getValue }) => <ProductPriceCell price={getValue()} />,
          sortUndefined: 'last',
          meta: {
            styles: { size: '1fr', minSize: 100, justify: 'end' },
            isResizable: true,
          },
        }),

        // Categories
        columnHelper.accessor(({ productCategories }: Product) => (productCategories.length > 0 ? productCategories : undefined), {
          id: ColumnIds.productsCategories,
          header: t('products:overview.columns.productCategories'),
          cell: ({ row: { original: product } }) => <ProductCategoriesCell categories={product.productCategories} />,
          filterFn: 'arrIncludesSome',
          sortUndefined: 'last',
          meta: {
            styles: { size: '2fr', minSize: 100 },
            isResizable: true,
            filterOptions: filters.categories,
          },
        }),

        // Options
        columnHelper.display({
          id: ColumnIds.options,
          cell: ({ row: { original: product } }) => <ProductOptionsCell dialogHandles={dialogHandles} product={product} />,
          meta: {
            styles: { size: 'max-content', minSize: 40 },
          },
        }),

        // Mobile
        columnHelper.display({
          id: ColumnIds.productsMobile,
          cell: ({ row: { original: product } }) => <ProductMobileCell dialogHandles={dialogHandles} product={product} />,
          meta: { mobile: true },
        }),
      ]),
    [dialogHandles, filters, t, user.lang],
  );
};

const useProductsTableFilters = () => {
  const { data: products } = useProducts();
  const productCategories = useProductCategories();

  const { t } = useTranslation(['lines', 'products']);
  const { compareForSort } = useIntl();

  const statusOptions = useMemo(
    () =>
      (['Approved', 'InReview', 'Rejected'] satisfies Product['status'][]).map((status) => ({
        label: t(`products:overview.columns.status.options.${status}`),
        value: status,
      })),
    [t],
  );

  const activityTypeOptions = useMemo(
    () =>
      [...new Set(products.map(({ activityType }) => activityType))].sort(compareForSort).map((activityType) => ({
        label: t(`lines:creativeWork.options.${activityType}.label`),
        value: t(`lines:creativeWork.options.${activityType}.label`),
      })),
    [compareForSort, products, t],
  );

  const categoryOptions = useMemo(
    () =>
      productCategories.map((category) => ({
        label: category,
        value: category,
      })),
    [productCategories],
  );

  return useMemo(
    () => ({
      statuses: statusOptions,
      activityTypes: activityTypeOptions,
      categories: categoryOptions,
    }),
    [activityTypeOptions, categoryOptions, statusOptions],
  );
};
